import { makeAutoObservable } from 'mobx'
import { logger } from 'shared/lib'
import type { IResponseConversationsQuickFilterKey } from 'entities/Conversation'
import type { EntitiesStore } from 'entities/EntitiesExport'
import { type ILocalTypeInbox } from 'entities/Inbox'

export class LocalInboxesStore {
  private _currentInboxId: IResponseConversationsQuickFilterKey | number | null = null

  constructor(private _entities: EntitiesStore) {
    makeAutoObservable(this)
  }

  initInboxQuickFilter = async () => {
    try {
      await this._entities.featureFlagsStore.fetchFeatureFlags()

      const key = window.localStorage.getItem(this._currentInboxStorageKey) as
        | IResponseConversationsQuickFilterKey
        | number
        | null

      if (!this._entities.featureFlagsStore.smart_views) {
        if (typeof key === 'number' && this._entities.inboxesStore.isUnifiedInbox) {
          this.setCurrentInbox('all')

          return
        }
      }

      if (key) {
        this.setCurrentInbox(key)
      } else if (this._entities.inboxesStore.isUnifiedInbox) {
        this.setCurrentInbox('all')
      }

      if (this.currentSmartViewInboxId) {
        this._entities.smartViewsStore.getItemAsync(this.currentSmartViewInboxId)
      }
    } catch (e) {
      logger.error(e)
    }
  }

  setCurrentInbox = (key: IResponseConversationsQuickFilterKey | number) => {
    const value = Number(key)

    this._currentInboxId = isNaN(value) ? key : value
    this._setStorageCurrentInbox(key)
  }

  resetCurrentInbox = () => {
    this._currentInboxId = null
    this._resetStoredCurrentInbox()
  }

  private _setStorageCurrentInbox = (key: IResponseConversationsQuickFilterKey | number) => {
    if (!this._entities.usersStore.user) return

    window.localStorage.setItem(this._currentInboxStorageKey, String(key))
  }

  private _resetStoredCurrentInbox = () => {
    if (!this._entities.usersStore.user) return

    window.localStorage.removeItem(this._currentInboxStorageKey)
  }

  private get _currentInboxStorageKey() {
    if (!this._entities.usersStore.user) return ''

    return `${this._entities.usersStore.user.id}_conversation_currentInboxId`
  }

  get currentInboxId() {
    return this._currentInboxId
  }

  get currentSmartViewInboxId() {
    if (!this._currentInboxId) return null
    if (typeof this._currentInboxId === 'string') return null

    return this._currentInboxId
  }

  get currentSmartView() {
    if (typeof this._currentInboxId !== 'number') return

    return this._entities.smartViewsStore.getItem(this._currentInboxId)
  }

  get hasQuickFilter() {
    if (this._entities.usersStore.user?.isShouldRequestAccess) return false

    if (typeof this._currentInboxId === 'string') return Boolean(this._currentInboxId)

    return false
  }

  get hasSmartViewFilter() {
    if (typeof this._currentInboxId === 'number') return Boolean(this._currentInboxId)

    return false
  }

  get typeInbox(): ILocalTypeInbox {
    if (this.hasSmartViewFilter) return 'smart_view'
    if (this.hasQuickFilter) return 'quick'

    return 'inbox'
  }

  get name() {
    if (this.hasQuickFilter) {
      switch (this.currentInboxId) {
        case 'all':
          return 'Chats'
        case 'calls':
          return 'Calls'
        case 'assigned':
          return 'Yours'
        case 'mentions':
          return 'Mentions'
        case 'unassigned':
          return 'Unassigned'
        case 'draft':
          return 'Drafts'
        default:
          return 'Chats'
      }
    }

    if (this.hasSmartViewFilter) {
      const item = this._entities.smartViewsStore.getItem(this.currentSmartViewInboxId)

      if (!item) return ''

      return item.name
    }

    return ''
  }

  get hasFilterAll() {
    return this._currentInboxId === 'all'
  }

  get hasFilterDraft() {
    return this._currentInboxId === 'draft'
  }

  get hasFilterAssigned() {
    return this._currentInboxId === 'assigned'
  }

  get hasFilterUnassigned() {
    return this._currentInboxId === 'unassigned'
  }

  get hasFilterMentions() {
    return this._currentInboxId === 'mentions'
  }
}
